import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Group,
  Stack,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { PARSABLE_FIELDS, type ParseEntityType } from '@seo/contracts';
import { useTRPC } from '../lib/trpc';
import { CreatePanel } from '../components/CreatePanel';
import { DataTable, type Column } from '../components/DataTable';
import {
  licenseInitialValues,
  licenseSchema,
  licenseValuesToInput,
  LicenseFields,
  paymentMethodInitialValues,
  paymentMethodSchema,
  paymentMethodValuesToInput,
  PaymentMethodFields,
  providerInitialValues,
  providerSchema,
  providerValuesToInput,
  ProviderFields,
  ReferenceRowEditor,
  rgOrgInitialValues,
  rgOrgSchema,
  rgOrgValuesToInput,
  RgOrgFields,
  TYPE_LABELS,
  useGeoOptions,
  useLicenseOptions,
  type EditableReferenceRow,
} from './ReferenceRowEditor';
import { zodResolver } from '../lib/zod-resolver';
import { ParsePanel } from '../components/ParsePanel';

/**
 * Який тип сутності парсера стоїть за кожним довідником.
 *
 * Мапа, а не збіг назв: у реєстрі вкладки звуться `pay`/`provider`/`license`/`rg`, а типи
 * парсера — повними іменами таблиць, і мовчазне зіставлення рядків розійшлося б на першому ж
 * перейменуванні вкладки.
 */
const PARSE_TYPE_OF: Record<EditableReferenceRow['kind'], ParseEntityType> = {
  pay: 'payment_method',
  provider: 'reference',
  license: 'license',
  rg: 'rg_organization',
};
import classes from './EntityPage.module.css';

interface PaymentMethodRow {
  id: string;
  name: string;
  type: string;
  currencies: string[];
  currencyCode: string | null;
  minDeposit: number | null;
  maxDeposit: number | null;
  minWithdrawal: number | null;
  maxWithdrawal: number | null;
  feeBps: number | null;
  processingTime: string | null;
  withdrawalSupported: boolean;
  bonusExcluded: boolean;
  hasLightLogo: boolean;
  geoCount: number;
}

interface ProviderRow {
  id: string;
  name: string;
  foundedYear: number | null;
  officialUrl: string | null;
  gameTypes: string[];
  catalogSource: string;
  catalogSyncCron: string | null;
  parserStatus: string;
  generateProviderPage: boolean;
  licenseCount: number;
}

interface LicenseRow {
  id: string;
  name: string;
  abbrFooter: string | null;
  jurisdiction: string;
  type: string;
  eeatRisk: string;
  registryUrl: string | null;
  registryCheckStatus: string | null;
  licenseNumberFormat: string | null;
  recognizedGeos: string[];
  siteRequirements: string[];
  footerTextTemplate: string | null;
}

interface RgRow {
  id: string;
  name: string;
  type: string;
  geoId: string | null;
  helpline: string | null;
  url: string | null;
  disclaimerOriginal: string | null;
  minAge: number | null;
  mandatoryInFooter: boolean;
  addToDictionary: boolean;
}

const RISK_COLOURS: Record<string, string> = { low: 'mint', medium: 'amber', high: 'rose' };

/**
 * Довідники — four tabs, as in the mockup (acceptance scenario 4).
 *
 * The payments tab carries the one column that changes behaviour rather than describing it:
 * a method without a light logo is flagged, because that is exactly what keeps it out of a
 * rendered build.
 *
 * Поля кожної форми, включно з мультиселектами ГЕО/ліцензій, живуть у `ReferenceRowEditor.tsx`
 * і монтуються тут тим самим компонентом, що й у картці редагування — так поле, додане один
 * раз, з'являється в обох формах за конструкцією (§ звіт 15.08).
 */
export function ReferencePage(): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<string | null>('pay');
  /**
   * Рядок, відкритий на редагування.
   *
   * Один стан на чотири вкладки: одночасно відкрита завжди одна картка, і перемикання
   * вкладки її закриває — інакше оператор бачив би картку ліцензії під таблицею платіжок.
   */
  const [editing, setEditing] = useState<EditableReferenceRow | null>(null);
  const [search, setSearch] = useState('');
  const [payOpen, setPayOpen] = useState(false);
  const [providerOpen, setProviderOpen] = useState(false);
  const [licenseOpen, setLicenseOpen] = useState(false);
  const [rgOpen, setRgOpen] = useState(false);

  const pagination = { limit: 100, direction: 'asc' as const };
  const searchInput = search ? { search } : {};

  const payments = useQuery(
    trpc.reference.paymentMethods.list.queryOptions({ ...searchInput, pagination }),
  );
  const providers = useQuery(
    trpc.reference.providers.list.queryOptions({ ...searchInput, pagination }),
  );
  const licenses = useQuery(
    trpc.reference.licenses.list.queryOptions({ ...searchInput, pagination }),
  );
  const rg = useQuery(
    trpc.reference.rgOrganizations.list.queryOptions({ ...searchInput, pagination }),
  );

  // ГЕО потрібні формі платіжки (мультиселект) і формі RG (одиничний вибір) — обидві тягнуть
  // список лише поки їхня панель створення відкрита. На двох інших вкладках цей запит нікому
  // не показують.
  const geos = useGeoOptions((tab === 'pay' && payOpen) || (tab === 'rg' && rgOpen));
  // Ліцензії — формі провайдера (мультиселект «Ліцензії»), так само лінивий гейт.
  const providerLicenses = useLicenseOptions(tab === 'providers' && providerOpen);

  const paymentForm = useForm({
    initialValues: paymentMethodInitialValues,
    validate: zodResolver(paymentMethodSchema),
  });
  const createPaymentMethod = useMutation(
    trpc.reference.paymentMethods.create.mutationOptions({
      onSuccess: async () => {
        paymentForm.reset();
        setPayOpen(false);
        await queryClient.invalidateQueries({ queryKey: trpc.reference.paymentMethods.pathKey() });
      },
    }),
  );

  const providerForm = useForm({
    initialValues: providerInitialValues,
    validate: zodResolver(providerSchema),
  });
  const createProvider = useMutation(
    trpc.reference.providers.create.mutationOptions({
      onSuccess: async () => {
        providerForm.reset();
        setProviderOpen(false);
        await queryClient.invalidateQueries({ queryKey: trpc.reference.providers.pathKey() });
      },
    }),
  );

  const licenseForm = useForm({
    initialValues: licenseInitialValues,
    validate: zodResolver(licenseSchema),
  });
  const createLicense = useMutation(
    trpc.reference.licenses.create.mutationOptions({
      onSuccess: async () => {
        licenseForm.reset();
        setLicenseOpen(false);
        await queryClient.invalidateQueries({ queryKey: trpc.reference.licenses.pathKey() });
      },
    }),
  );

  const rgOrgForm = useForm({
    initialValues: rgOrgInitialValues,
    validate: zodResolver(rgOrgSchema),
  });
  const createRgOrganization = useMutation(
    trpc.reference.rgOrganizations.create.mutationOptions({
      onSuccess: async () => {
        rgOrgForm.reset();
        setRgOpen(false);
        await queryClient.invalidateQueries({ queryKey: trpc.reference.rgOrganizations.pathKey() });
      },
    }),
  );

  const paymentColumns: Column<PaymentMethodRow>[] = [
    { key: 'name', header: 'Метод', render: (row) => <span>{row.name}</span> },
    {
      key: 'type',
      header: 'Тип',
      render: (row) => (
        <Badge size="sm" variant="default">
          {TYPE_LABELS[row.type] ?? row.type}
        </Badge>
      ),
    },
    {
      key: 'currencies',
      header: 'Валюти',
      render: (row) => <span className={classes.mono}>{row.currencies.join(', ') || '—'}</span>,
    },
    {
      key: 'processing',
      header: 'Час',
      render: (row) => <span className={classes.dim}>{row.processingTime ?? '—'}</span>,
    },
    {
      key: 'withdrawal',
      header: 'Виведення',
      render: (row) => (
        <Text className={row.withdrawalSupported ? classes.mono : classes.dim}>
          {row.withdrawalSupported ? 'так' : 'ні'}
        </Text>
      ),
    },
    {
      // The column that decides whether the method reaches a page at all (§1.1).
      key: 'logo',
      header: 'Лого',
      render: (row) =>
        row.hasLightLogo ? (
          <Text className={classes.mono}>✓</Text>
        ) : (
          <Badge color="rose" size="sm" data-testid={`pm-nologo-${row.name}`}>
            без svg_light
          </Badge>
        ),
    },
    {
      key: 'geo',
      header: 'ГЕО',
      align: 'right',
      render: (row) => (
        <span className={classes.mono} data-testid={`pm-geocount-${row.name}`}>
          {row.geoCount}
        </span>
      ),
    },
  ];

  const providerColumns: Column<ProviderRow>[] = [
    { key: 'name', header: 'Провайдер', render: (row) => <span>{row.name}</span> },
    {
      key: 'founded',
      header: 'Заснований',
      render: (row) => <span className={classes.mono}>{row.foundedYear ?? '—'}</span>,
    },
    {
      key: 'types',
      header: 'Типи ігор',
      render: (row) => <span className={classes.dim}>{row.gameTypes.join(', ') || '—'}</span>,
    },
    {
      key: 'licenses',
      header: 'Ліцензій',
      align: 'right',
      render: (row) => <span className={classes.mono}>{row.licenseCount}</span>,
    },
    {
      key: 'page',
      header: 'Своя сторінка',
      render: (row) =>
        row.generateProviderPage ? (
          <Badge size="sm" data-testid={`provider-page-${row.name}`}>
            генерується
          </Badge>
        ) : (
          <Text className={classes.dim}>—</Text>
        ),
    },
  ];

  const licenseColumns: Column<LicenseRow>[] = [
    { key: 'name', header: 'Регулятор', render: (row) => <span>{row.name}</span> },
    {
      key: 'abbr',
      header: 'У футері',
      render: (row) => <span className={classes.mono}>{row.abbrFooter ?? '—'}</span>,
    },
    { key: 'jurisdiction', header: 'Юрисдикція', render: (row) => <span>{row.jurisdiction}</span> },
    {
      key: 'type',
      header: 'Тип',
      render: (row) => (
        <Badge size="sm" variant="default">
          {row.type === 'local' ? 'локальна' : 'офшорна'}
        </Badge>
      ),
    },
    {
      key: 'risk',
      header: 'E-E-A-T ризик',
      render: (row) => (
        <Badge size="sm" color={RISK_COLOURS[row.eeatRisk] ?? 'gray'}>
          {row.eeatRisk}
        </Badge>
      ),
    },
  ];

  const rgColumns: Column<RgRow>[] = [
    { key: 'name', header: 'Організація', render: (row) => <span>{row.name}</span> },
    {
      key: 'type',
      header: 'Тип',
      render: (row) => <span className={classes.dim}>{row.type}</span>,
    },
    {
      key: 'helpline',
      header: 'Гаряча лінія',
      render: (row) => <span className={classes.mono}>{row.helpline || '—'}</span>,
    },
    {
      key: 'age',
      header: 'Вік',
      align: 'right',
      render: (row) => <span className={classes.mono}>{row.minAge ? `${row.minAge}+` : '—'}</span>,
    },
    {
      key: 'footer',
      header: 'Обовʼязково',
      render: (row) =>
        row.mandatoryInFooter ? (
          <Badge size="sm">у футері</Badge>
        ) : (
          <Text className={classes.dim}>—</Text>
        ),
    },
  ];

  return (
    <Stack gap="md">
      <Group gap="sm">
        <TextInput
          placeholder="пошук…"
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
          }}
          data-testid="reference-search"
          style={{ maxWidth: 260 }}
        />
      </Group>

      <Tabs
        value={tab}
        onChange={(value) => {
          setTab(value);
          setEditing(null);
        }}
        data-testid="reference-tabs"
      >
        <Tabs.List>
          <Tabs.Tab value="pay" data-testid="tab-pay">
            Платіжки
          </Tabs.Tab>
          <Tabs.Tab value="providers" data-testid="tab-providers">
            Провайдери
          </Tabs.Tab>
          <Tabs.Tab value="licenses" data-testid="tab-licenses">
            Ліцензії
          </Tabs.Tab>
          <Tabs.Tab value="rg" data-testid="tab-rg">
            RG
          </Tabs.Tab>
        </Tabs.List>

        <Box mt="md">
          <Tabs.Panel value="pay">
            <Stack gap="sm">
              <CreatePanel
                onReset={() => {
                  createPaymentMethod.reset();
                  paymentForm.reset();
                }}
                zone="geo_dictionaries"
                label="Додати метод"
                testId="payment-method"
                open={payOpen}
                onOpenChange={setPayOpen}
                busy={createPaymentMethod.isPending}
                error={createPaymentMethod.error}
                onSubmit={paymentForm.onSubmit((values) => {
                  createPaymentMethod.mutate(paymentMethodValuesToInput(values));
                })}
              >
                <TextInput
                  size="xs"
                  label="Назва"
                  placeholder="назва"
                  data-testid="payment-method-name"
                  style={{ width: 160 }}
                  {...paymentForm.getInputProps('name')}
                />
                <PaymentMethodFields form={paymentForm} idPrefix="payment-method" geos={geos} />
              </CreatePanel>

              <DataTable
                rows={payments.data?.items ?? []}
                columns={paymentColumns}
                loading={payments.isPending}
                error={payments.error}
                emptyMessage="Платіжних методів немає. Додайте перший — і ГЕО-пресети зможуть на нього послатися."
                rowTestId={(row) => `pm-row-${row.name}`}
                testId="pm-table"
                onRowClick={(row) => {
                  setEditing({
                    kind: 'pay',
                    id: row.id,
                    name: row.name,
                    type: row.type,
                    currencies: row.currencies,
                    currencyCode: row.currencyCode,
                    minDeposit: row.minDeposit,
                    maxDeposit: row.maxDeposit,
                    minWithdrawal: row.minWithdrawal,
                    maxWithdrawal: row.maxWithdrawal,
                    feeBps: row.feeBps,
                    processingTime: row.processingTime,
                    withdrawalSupported: row.withdrawalSupported,
                    bonusExcluded: row.bonusExcluded,
                  });
                }}
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="providers">
            <Stack gap="sm">
              <CreatePanel
                onReset={() => {
                  createProvider.reset();
                  providerForm.reset();
                }}
                zone="geo_dictionaries"
                label="Додати провайдера"
                testId="provider"
                open={providerOpen}
                onOpenChange={setProviderOpen}
                busy={createProvider.isPending}
                error={createProvider.error}
                onSubmit={providerForm.onSubmit((values) => {
                  createProvider.mutate(providerValuesToInput(values));
                })}
              >
                <TextInput
                  size="xs"
                  label="Назва"
                  placeholder="назва"
                  data-testid="provider-name"
                  style={{ width: 180 }}
                  {...providerForm.getInputProps('name')}
                />
                <ProviderFields form={providerForm} idPrefix="provider" licenses={providerLicenses} />
              </CreatePanel>

              <DataTable
                rows={providers.data?.items ?? []}
                columns={providerColumns}
                loading={providers.isPending}
                error={providers.error}
                emptyMessage="Провайдерів немає. Без них ігрова сітка не збереться."
                rowTestId={(row) => `provider-row-${row.name}`}
                testId="provider-table"
                onRowClick={(row) => {
                  setEditing({
                    kind: 'provider',
                    id: row.id,
                    name: row.name,
                    foundedYear: row.foundedYear,
                    gameTypes: row.gameTypes,
                    generateProviderPage: row.generateProviderPage,
                    officialUrl: row.officialUrl,
                    catalogSource: row.catalogSource,
                    catalogSyncCron: row.catalogSyncCron,
                  });
                }}
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="licenses">
            <Stack gap="sm">
              <CreatePanel
                onReset={() => {
                  createLicense.reset();
                  licenseForm.reset();
                }}
                zone="geo_dictionaries"
                label="Додати регулятора"
                testId="license"
                open={licenseOpen}
                onOpenChange={setLicenseOpen}
                busy={createLicense.isPending}
                error={createLicense.error}
                onSubmit={licenseForm.onSubmit((values) => {
                  createLicense.mutate(licenseValuesToInput(values));
                })}
              >
                <TextInput
                  size="xs"
                  label="Назва"
                  placeholder="назва"
                  data-testid="license-name"
                  style={{ width: 200 }}
                  {...licenseForm.getInputProps('name')}
                />
                <LicenseFields form={licenseForm} idPrefix="license" />
              </CreatePanel>

              <DataTable
                rows={licenses.data?.items ?? []}
                columns={licenseColumns}
                loading={licenses.isPending}
                error={licenses.error}
                emptyMessage="Регуляторів немає. Футер сайту без ліцензії не пройде E-E-A-T."
                rowTestId={(row) => `license-row-${row.name}`}
                testId="license-table"
                onRowClick={(row) => {
                  setEditing({
                    kind: 'license',
                    id: row.id,
                    name: row.name,
                    jurisdiction: row.jurisdiction,
                    type: row.type,
                    abbrFooter: row.abbrFooter,
                    eeatRisk: row.eeatRisk,
                    registryUrl: row.registryUrl,
                    registryCheckStatus: row.registryCheckStatus,
                    licenseNumberFormat: row.licenseNumberFormat,
                    recognizedGeos: row.recognizedGeos,
                    siteRequirements: row.siteRequirements,
                    footerTextTemplate: row.footerTextTemplate,
                  });
                }}
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="rg">
            <Stack gap="sm">
              <CreatePanel
                onReset={() => {
                  createRgOrganization.reset();
                  rgOrgForm.reset();
                }}
                zone="geo_dictionaries"
                label="Додати організацію"
                testId="rg-org"
                open={rgOpen}
                onOpenChange={setRgOpen}
                busy={createRgOrganization.isPending}
                error={createRgOrganization.error}
                onSubmit={rgOrgForm.onSubmit((values) => {
                  createRgOrganization.mutate(rgOrgValuesToInput(values));
                })}
              >
                <TextInput
                  size="xs"
                  label="Назва"
                  placeholder="назва"
                  data-testid="rg-org-name"
                  style={{ width: 200 }}
                  {...rgOrgForm.getInputProps('name')}
                />
                <RgOrgFields form={rgOrgForm} idPrefix="rg-org" geos={geos} />
              </CreatePanel>

              <DataTable
                rows={rg.data?.items ?? []}
                columns={rgColumns}
                loading={rg.isPending}
                error={rg.error}
                emptyMessage="RG-організацій немає. Обовʼязковий блок відповідальної гри не збереться."
                rowTestId={(row) => `rg-row-${row.name}`}
                testId="rg-table"
                onRowClick={(row) => {
                  setEditing({
                    kind: 'rg',
                    id: row.id,
                    name: row.name,
                    type: row.type,
                    geoId: row.geoId,
                    helpline: row.helpline,
                    url: row.url,
                    disclaimerOriginal: row.disclaimerOriginal,
                    minAge: row.minAge,
                    mandatoryInFooter: row.mandatoryInFooter,
                    addToDictionary: row.addToDictionary,
                  });
                }}
              />
            </Stack>
          </Tabs.Panel>
        </Box>
      </Tabs>

      {/*
       * Одна картка на сторінку, а не по одній під кожною таблицею.
       *
       * `Tabs.Panel` лишає панелі змонтованими, тож копія під кожною з чотирьох таблиць
       * означала чотири однакові картки в DOM — і чотири однакові `data-testid`.
       */}
      {editing === null ? null : (
        <>
          <ReferenceRowEditor
            key={editing.id}
            row={editing}
            onClose={() => {
              setEditing(null);
            }}
          />

          {/*
           * AI-парсинг довідників. Досі він умів рівно одну сутність із чотирьох —
           * провайдерів, — і платіжки, ліцензії та RG заповнювались тільки руками, попри те
           * що їхні дані живуть на публічних сторінках регуляторів і платіжних систем.
           *
           * Кожен довідник — свій тип сутності, а не спільний `reference`: колонки різні, і
           * спільний тип означав би дозвіл писати в поле, якого в цієї сутності немає.
           */}
          <ParsePanel
            key={`parse-${editing.id}`}
            entityType={PARSE_TYPE_OF[editing.kind]}
            entityId={editing.id}
            targets={[...PARSABLE_FIELDS[PARSE_TYPE_OF[editing.kind]]]}
          />
        </>
      )}
    </Stack>
  );
}
